import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const StudyForm = ({ study, sites, onSave, onCancel, isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: study?.name || '',
    protocol_number: study?.protocol_number || '',
    sponsor: study?.sponsor || '',
    indication: study?.indication || '',
    phase: study?.phase || '',
    therapeutic_area: study?.therapeutic_area || '',
    study_lead: study?.study_lead || '',
    sites: study?.sites || [],
    status: study?.status || 'active',
    description: study?.description || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(study?.id, formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSiteSelection = (siteId, checked) => {
    if (checked) {
      setFormData(prev => ({ ...prev, sites: [...prev.sites, siteId] }));
    } else {
      setFormData(prev => ({ ...prev, sites: prev.sites.filter(id => id !== siteId) }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="study-name">Study Name *</Label>
          <Input
            id="study-name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter study name"
            required
          />
        </div>
        <div>
          <Label htmlFor="protocol">Protocol Number</Label>
          <Input
            id="protocol"
            value={formData.protocol_number}
            onChange={(e) => handleChange('protocol_number', e.target.value)}
            placeholder="Enter protocol number"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="sponsor">Sponsor</Label>
          <Input
            id="sponsor"
            value={formData.sponsor}
            onChange={(e) => handleChange('sponsor', e.target.value)}
            placeholder="Enter sponsor name"
          />
        </div>
        <div>
          <Label htmlFor="indication">Indication</Label>
          <Input
            id="indication"
            value={formData.indication}
            onChange={(e) => handleChange('indication', e.target.value)}
            placeholder="Enter indication"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="phase">Phase</Label>
          <Select value={formData.phase} onValueChange={(value) => handleChange('phase', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select phase" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Phase I">Phase I</SelectItem>
              <SelectItem value="Phase II">Phase II</SelectItem>
              <SelectItem value="Phase III">Phase III</SelectItem>
              <SelectItem value="Phase IV">Phase IV</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="therapeutic_area">Therapeutic Area</Label>
          <Input
            id="therapeutic_area"
            value={formData.therapeutic_area}
            onChange={(e) => handleChange('therapeutic_area', e.target.value)}
            placeholder="Enter therapeutic area"
          />
        </div>
        <div>
          <Label htmlFor="study_lead">Study Lead</Label>
          <Input
            id="study_lead"
            value={formData.study_lead}
            onChange={(e) => handleChange('study_lead', e.target.value)}
            placeholder="Enter study lead"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select value={formData.status} onValueChange={(value) => handleChange('status', value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Enter study description"
          rows={3}
        />
      </div>

      <div>
        <Label>Associated Sites</Label>
        <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto border rounded-lg p-3">
          {sites.map(site => (
            <div key={site.id} className="flex items-center space-x-2">
              <input
                type="checkbox"
                id={`study-site-${site.id}`}
                checked={formData.sites.includes(site.id)}
                onChange={(e) => handleSiteSelection(site.id, e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor={`study-site-${site.id}`} className="text-sm">
                {site.name}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">
          {isEdit ? 'Update Study' : 'Create Study'}
        </Button>
      </div>
    </form>
  );
};
